import { ref as dbRef, onValue } from 'firebase/database';
import { db } from '../firebase';

// Everything around a bug report that could even remotely help triage it.
// Matt, 2026-10-10: "make sure that we are always passing in any information
// that might or even remotely could be relevant to any bug report."
//
// The engine state already rides along whole as `gameState`; this is the
// rest - where the reporter is, who they are AT THIS TABLE, what the screen
// was asking of them, the room and its settings, connectivity, and the last
// few errors the page threw. Compact on purpose (no other players' emails,
// no tokens), and every piece is best-effort: gathering must never be able
// to stop a report from sending.

const MAX_ERRORS = 8;
const recentErrors = [];
let firebaseConnected = null;
let connectedSince = null;
let lastDisconnectAt = null;
let installed = false;

const short = (value, n = 300) => String(value ?? '').slice(0, n);

export const noteError = (kind, message, extra) => {
  try {
    recentErrors.push({
      kind, message: short(message), ...(extra ? { where: short(extra, 200) } : {}), at: new Date().toISOString(),
    });
    if (recentErrors.length > MAX_ERRORS) recentErrors.splice(0, recentErrors.length - MAX_ERRORS);
  } catch {
    // never let error bookkeeping become an error
  }
};

export const recentErrorList = () => recentErrors.slice();

/** Called once from main.js: window errors, rejections, and RTDB connection. */
export function installBugContext() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', (e) => {
    noteError('error', e?.message || e?.error?.message, e?.filename ? `${e.filename}:${e.lineno}:${e.colno}` : null);
  });
  window.addEventListener('unhandledrejection', (e) => {
    const r = e?.reason;
    noteError('unhandledrejection', r?.message || r);
  });
  try {
    if (db) {
      onValue(dbRef(db, '.info/connected'), (snap) => {
        const now = snap.val() === true;
        if (now && firebaseConnected !== true) connectedSince = new Date().toISOString();
        if (!now && firebaseConnected === true) lastDisconnectAt = new Date().toISOString();
        firebaseConnected = now;
      });
    }
  } catch {
    // connection status is a nicety
  }
}

const safe = (fn) => {
  try { return fn(); } catch (err) { return `unavailable: ${short(err?.message, 80)}`; }
};

const openDialogs = () => {
  const els = Array.from(document.querySelectorAll('[role="dialog"], .modal, dialog[open]'));
  return els
    .map((el) => el.getAttribute('aria-label') || el.className || el.tagName)
    .map((s) => short(s, 60))
    .filter((s) => s !== 'Report a bug');
};

/**
 * Pure-ish: reads a Vuex store (state + getters) and the browser. Returns a
 * compact object; any section that throws becomes an "unavailable" string.
 */
export function gatherBugContext(store) {
  const { state = {}, getters = {} } = store || {};
  const ctx = {};

  ctx.screen = safe(() => {
    let name = 'menu';
    if (!state.authReady) name = 'loading-auth';
    else if (!getters.isApproved) name = 'site-gate';
    else if (state.roomId && getters.inLobby) name = 'lobby';
    else if (state.game && getters.finished) name = 'game-finished';
    else if (state.game) name = 'game';
    else if (state.roomId) name = 'room-loading';
    return name;
  });

  ctx.account = safe(() => ({
    signedIn: Boolean(state.user),
    uid: state.user?.uid || null,
    approval: state.siteUser?.status || null,
    isAdmin: Boolean(getters.isAdmin),
    authReady: Boolean(state.authReady),
    siteUserReady: Boolean(state.siteUserReady),
  }));

  ctx.room = safe(() => {
    const meta = state.roomMeta;
    if (!state.roomId && !meta) return null;
    return {
      roomId: state.roomId || null,
      roomCode: meta?.roomCode || null,
      status: meta?.status || null,
      async: Boolean(meta?.async),
      iAmHost: Boolean(getters.isHost),
      hostUid: meta?.hostUid || null,
      wordSet: getters.wordSetKey || null,
      wordSetName: getters.wordSetName || null,
      createdAt: meta?.createdAt || null,
      lastActiveAt: meta?.lastActiveAt || null,
      seats: (getters.seats || []).map((s) => ({
        name: s.displayName || null, uid: s.uid, team: s.team, role: s.role,
      })),
      startProblem: getters.startProblem || null,
      presentUids: Object.keys(state.presence || {}).filter((k) => state.presence[k]),
      roomError: state.roomError ? short(state.roomError) : null,
      generatingWords: Boolean(state.generatingWords),
    };
  });

  ctx.me = safe(() => {
    const me = getters.me;
    if (!me) return null;
    return {
      name: me.name || me.displayName || null,
      seat: me.seat ?? me.seatIndex ?? null,
      team: getters.myTeam || null,
      role: me.role || null,
      myTurnToClue: Boolean(getters.iGiveClue),
      myTurnToGuess: Boolean(getters.iGuess),
      iDriveFinish: getters.driverUid ? getters.driverUid === state.user?.uid : null,
    };
  });

  ctx.game = safe(() => {
    const g = state.game;
    if (!g) return null;
    return {
      phase: g.meta?.phase || null,
      winner: g.meta?.winner || null,
      endedBy: g.meta?.endedBy || null,
      seq: g.meta?.seq ?? null,
      startingTeam: g.startingTeam || null,
      turnTeam: g.turn?.team || null,
      turnPhase: g.turn?.phase || null,
      clue: g.turn?.clue || null,
      guessesLeft: g.turn?.guessesLeft ?? null,
      guessedThisTurn: g.turn?.guessedThisTurn ?? null,
      wordsLeft: getters.wordsLeft || null,
      revealedCount: (g.revealed || []).filter(Boolean).length,
      playerCount: (g.players || []).length,
      logLength: (g.log || []).length,
    };
  });

  ctx.ui = safe(() => ({
    openDialogs: openDialogs(),
    focused: document.activeElement
      ? short(`${document.activeElement.tagName.toLowerCase()}${document.activeElement.className ? `.${document.activeElement.className}` : ''}`, 80)
      : null,
    visibility: document.visibilityState || null,
    scrollY: Math.round(window.scrollY || 0),
  }));

  ctx.network = safe(() => ({
    online: typeof navigator.onLine === 'boolean' ? navigator.onLine : null,
    firebaseConnected,
    connectedSince,
    lastDisconnectAt,
    connection: navigator.connection?.effectiveType || null,
  }));

  ctx.device = safe(() => ({
    standalone: Boolean(window.matchMedia?.('(display-mode: standalone)')?.matches || navigator.standalone),
    orientation: window.screen?.orientation?.type || null,
    language: navigator.language || null,
    timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone || null,
    reducedMotion: Boolean(window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches),
    serviceWorker: Boolean(navigator.serviceWorker?.controller),
  }));

  ctx.build = safe(() => ({
    version: process.env.VUE_APP_GIT_SHA || 'unknown',
    builtAt: process.env.VUE_APP_BUILD_TIME || null,
    mode: process.env.NODE_ENV || null,
  }));

  ctx.recentErrors = safe(() => recentErrorList());
  ctx.pageAgeSec = safe(() => Math.round((performance?.now?.() || 0) / 1000));
  ctx.referrer = safe(() => short(document.referrer, 200) || null);

  return ctx;
}
